import { styled, Typography, Box, useTheme, useMediaQuery } from '@mui/material'
import CardGraph, { CardGraphProps } from './Card.graph'
import { StringUtils } from '../../../utils/commons.utils'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import constants from '../../../constants'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const ValueTypo = styled(Typography)(() => ({
  textAlign: 'center',
  fontSize: '40px',
  fontWeight: 900,
  lineHeight: '1.10',
}))

const SubtitleTypo = styled(Typography)(({ theme }) => ({
  color: theme.palette.blockLabel,
  textAlign: 'center',
  fontSize: '12px',
  fontWeight: 400,
}))

const PieBox = styled(Box, {
  shouldForwardProp: (props) => props !== 'focusIndex',
})<{ focusIndex: number }>(({ focusIndex }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  'path:focus': {
    outline: 'none !important;',
  },
  // remove title margin (because of the PieChart margin...)
  marginTop: '-35px',
  'recharts-tooltip-wrapper': { zIndex: 1 },
  [`.legend-item-${focusIndex}`]: {
    fontWeight: 700,
  },
  [`.recharts-pie-labels .recharts-layer:nth-of-type(${focusIndex + 1})`]: {
    fontWeight: 700,
  },
  '.recharts-wrapper svg': {
    overflow: 'visible !important;',
  },
}))
const TooltipContainer = styled(Box)({
  padding: '17px',
  backgroundColor: 'rgba(54, 54, 54, 0.74)',
  backdropFilter: 'blur(7px)',
  borderRadius: '5px',
  color: 'white',
})

export interface PieGraphProps extends CardGraphProps {
  value?: number | string
  subtitle?: string
  data: { name: string; value: string | number; metaData?: any }[]
  showPercent?: boolean
  children?: React.ReactNode | React.ReactNode[]
  unit?: string
  half?: boolean
  minPercentShown?: number
  tooltipContent?: (data: { name: string; value: string | number; metaData?: any }) => JSX.Element
}

const PieGraph = (props: PieGraphProps) => {
  const {
    unit,
    half,
    tooltipContent,
    children,
    value,
    subtitle,
    data,
    showPercent,
    minPercentShown,
    ...cardProps
  } = { minPercentShown: 0.05, ...props }
  const theme = useTheme()
  const onlyXs = useMediaQuery(theme.breakpoints.only('xs'))

  const [legendHeight, setLegendHeigth] = useState<number>(0)
  const boxRef = useRef<HTMLElement>(null)
  const { t } = useTranslation()

  const [focusIndex, setFocusIndex] = useState(-1)
  const total = useMemo(() => {
    return data.filter(({ value }) => !!value).reduce((total, val) => total + Number(val.value), 0)
  }, [data])
  const textValue = useCallback(
    (val: string | number, splitted = false) => {
      const text = `${val}${unit ? unit : ''}`
      const percentage =
        showPercent && total
          ? `(${t('global:format.percent', {
              value: ((Number(val) / total) * 100).toFixed(1),
            })})`
          : ''

      if (splitted) {
        return [text, percentage]
      }
      return `${text} ${percentage}`
    },
    [showPercent, total, unit, t],
  )
  const usedData = useMemo(
    () =>
      data
        .filter(({ value }) => !!value)
        .map((data) => ({
          name: `${data.name} : ${textValue(data.value)}`,
          value: data.value,
        })),
    [textValue, data],
  )

  useEffect(() => {
    function handleResize() {
      setTimeout(() => {
        if (boxRef.current) {
          const refElement = boxRef.current.getElementsByClassName('recharts-legend-wrapper')
          if (refElement && refElement[0]) {
            setLegendHeigth(refElement[0].getBoundingClientRect()?.height ?? 0)
          }
        }
      }, 50)
    }

    if (usedData) {
      handleResize()
    } else {
      setLegendHeigth(0)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [usedData])

  const computedTooltipContent = ({ active, payload }: any) => {
    if (active && payload && payload[0]?.payload && tooltipContent) {
      return <TooltipContainer>{tooltipContent(payload[0]?.payload)}</TooltipContainer>
    }
    return null
  }

  const computedLabelContent = useCallback(
    ({ value: val, cy, textAnchor, stroke, cx, name, x, y, fill }: any) => {
      if (total && Number(val) / total < minPercentShown) {
        return undefined
      }

      let textComponent = (
        <tspan x={x} dy="0em">
          {textValue(val)}
        </tspan>
      )
      if (onlyXs) {
        const textValues = textValue(val, true)
        if (textValues.length === 2) {
          textComponent = (
            <>
              <tspan x={x} dy="-0.3em">
                {textValues[0]}
              </tspan>
              <tspan x={x} dy="1.2em">
                {textValues[1]}
              </tspan>
            </>
          )
        }
      }

      return (
        <text
          cy={cy}
          stroke={stroke}
          cx={cx}
          name={name}
          x={x}
          y={y}
          fill={fill}
          fontSize={onlyXs ? '12px' : '16px'}
          textAnchor={textAnchor}
          alignmentBaseline="middle"
          className="recharts-text recharts-pie-label-text">
          {textComponent}
        </text>
      )
    },
    [minPercentShown, textValue, total, onlyXs],
  )
  const height = half ? 125 : 260
  const valueTop = half ? '80px' : '135px'
  return (
    <CardGraph {...cardProps}>
      <PieBox ref={boxRef} focusIndex={focusIndex}>
        <ResponsiveContainer height={height + legendHeight} width="100%">
          {/* margin required for export visible ... */}
          <PieChart height={height} margin={{ top: 35, right: 0, bottom: 25, left: 0 }}>
            <Pie
              isAnimationActive={false}
              dataKey="value"
              nameKey="name"
              startAngle={half ? 180 : 360}
              endAngle={0}
              data={usedData}
              cy={100}
              outerRadius={100}
              innerRadius={84}
              legendType="circle"
              labelLine={({ cy, stroke, fill, cx, points, value: val }) => {
                if (total && Number(val) / total < minPercentShown) {
                  return <></>
                }
                return (
                  <path
                    cy={cy}
                    stroke={stroke}
                    fill={fill}
                    cx={cx}
                    className="recharts-curve recharts-pie-label-line"
                    d={`M${points[0].x},${points[0].y}L${points[1].x},${points[1].y}`}
                  />
                )
              }}
              label={computedLabelContent}>
              {usedData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={constants.colors.graphs[index % constants.colors.graphs.length]}
                  stroke={constants.colors.graphs[index % constants.colors.graphs.length]}
                  strokeWidth={focusIndex === index ? 4 : 1}
                  onMouseLeave={() => {
                    setFocusIndex(-1)
                  }}
                  onMouseEnter={() => {
                    setFocusIndex(index)
                  }}
                />
              ))}
            </Pie>
            <Tooltip content={computedTooltipContent} />
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{ fontSize: '14px', bottom: 'none', top: height }}
              onMouseEnter={(_, index) => {
                setFocusIndex(index)
              }}
              onMouseLeave={() => {
                setFocusIndex(-1)
              }}
            />
          </PieChart>
        </ResponsiveContainer>

        {value !== undefined && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'absolute',
              top: valueTop,
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 0,
            }}>
            <ValueTypo>{StringUtils.formatNumber(value)}</ValueTypo>
            {subtitle && <SubtitleTypo>{subtitle}</SubtitleTypo>}
          </Box>
        )}
      </PieBox>
      {children}
    </CardGraph>
  )
}

export default PieGraph
